
import React from 'react';
import equal from 'deep-equal';
import { Validator } from 'jsonschema';

import Resolver from '../component_resolver.jsx';
import * as FormComponent from "../form_components.jsx";
import Tooltip from "../../common/Tooltip.jsx";
import { FormContext } from "../form_context.jsx";
import Setting from './setting.jsx';
import {InvalidConfigComponentPlaceholder, InvalidConfigMessage} from './invalid_configuration.jsx';
import Helpers from './helpers.jsx';
import { DataType, Mode, ViewMode, Format, FormatRegExp, castDataType, CONTEXT_OPTIONS } from '../common.jsx';
import TooltipWithRef from '../../common/Tooltip.jsx';
import { datagrid_show_path } from '~/routes';
import CollapsibleSection from '../../common/CollapsibleSection.jsx';

    /**
     * Performs equality by iterating through keys on an object and returning false
     * when any key has values which are not strictly equal between the arguments.
     * Returns true when the values of all keys are strictly equal.
     */
    const shallowEqual = (objA, objB) => {
      if (Object.is(objA, objB)) {
        return true;
      }
    
      if (typeof objA !== 'object' || objA === null ||
          typeof objB !== 'object' || objB === null) {
        return false;
      }
    
      const keysA = Object.keys(objA);
      const keysB = Object.keys(objB);
    
      if (keysA.length !== keysB.length) {
        return false;
      }
    
      // Test for A's keys different from B.
      for (let i = 0; i < keysA.length; i++) {
        if (
          !hasOwnProperty.call(objB, keysA[i]) ||
          !Object.is(objA[keysA[i]], objB[keysA[i]])
        ) {
          return false;
        }
      }
    
      return true;
    }

    function shallowCompareAtPath(obj1, obj2, pathStr) {
      const path = pathStr
        .replace(/\]/g, '')        // remove closing brackets
        .split(/\[|\./)            // split on [ or .
        .filter(Boolean);          // remove empty strings
    
      const lastKey = path.pop();
    
      const parent1 = path.reduce((o, k) => o?.[k], obj1);
      const parent2 = path.reduce((o, k) => o?.[k], obj2);
    
      return parent1?.[lastKey] === parent2?.[lastKey];
    }

    const mapNameFromId = (c, findComponentsCallback) => {
      const id = (c.match(/id-.+?(?=\])/) || [])[0];
      const component = id ? findComponentsCallback({id: id})[0] : c;
      if (!component) {
        // This could happen if there's a broken dependency, i.e. component has been deleted.
        return null;
      }
      return id ? c.replace(id, component.name) : c;
    }

    /** 
     * Performance improvements to handle the update logic. 
     * The components receive a bunch of unused props, that isn't used. 
     * They still trigger render without these checks, which really slows down long forms
    */
    const shouldUpdate = (props, nextProps, state, nextState, answerCheck) => {
      if (!shallowEqual(state, nextState)) { return true }

      // Always render components in inexpensive mode
      if ([Mode.SETTINGS, Mode.TOOLBAR_ITEM].includes(nextProps.mode)) {
        return true;
      }

      const keysA = Object.keys(props);
      const keysB = Object.keys(nextProps);

      if (keysA.length !== keysB.length) {
        return true;
      } 

      // Check prop changes
      for (let i = 0; i < keysB.length; i++) {

        // Deep check answers (later) as the reference is always updated.
        // Content updates only affect the selected item. Selected item is always rendered.
        if(['answers', 'content'].includes(keysB[i])){ continue; }

        // Selected id updates the whole form unless we check that it's relevant (for this or children)
        // Always update selected/unselected item, as it's mostly likely a content change
        if (keysB[i] == 'selectedId'){
          let nestedIds = Resolver.componentClass(props.content.type).nestedComponents(props.content).map(c => c.id)
          nestedIds.push(props.content.id);
          if (nestedIds.includes(nextProps.selectedId) || nestedIds.includes(props.selectedId)) {
            return true;
          }
        }
        // Shallow check for non complex
        else if (!shallowEqual(nextProps[keysB[i]], props[keysB[i]])) {
          return true;
        }
      }

      // Check deep changes for complex props
      if (props.mode == Mode.FILL || props.missingAttachments) { // Only interesting in FILL mode
        return answerCheck(nextProps.answers, props.answers)
      }
      return false;
    }


    /*
     * Base class for form components
     */
    export default class Base extends React.Component {
        static contextType = FormContext;

        static get TRANSLATED_ATTRIBUTES() {
            return {};
        }

        // Whether the component should receive answers as props
        // Performance optiomisation for static components
        static get ANSWER_DEPENDENT() {
          return true
        }

        // JSON schema to validate invalid configuration that user can save, but will lead to failures when trying to use the form.
        // TODO: Currently only contains datagrid related stuff. 
        // Probably should be refactored to own logic with other datagrid setting related stuff
        static get CONFIG_VALIDATION_SCHEMA() {
          return {
            "type": "object",
            "if": {
              "properties": {
                "useContextual": { "const": CONTEXT_OPTIONS.DATAGRID_PRESET }
              },
              "required": ["useContextual"]
            },
            "then": {
              "required": ["datagridId"],
              "properties": {
                "datagridId": {"type": "integer"}
              },      
              "if": {
                "required": ["datagridId"]
              },
              "then": {
                "required": ["columnAccessor"],
                "properties": {
                  "presetId": { "type": ["integer"] },
                  "columnAccessor": { "type": "string", "minLength": 1 }
                },
                "if": {
                  "required": ["columnAccessor"]
                },
                "then": {
                  "required": ["dependencyMapping"],
                    "properties": {
                              "dependencyMapping": {
                                "type": "array",
                                "items": {
                                  "type": "object",
                                  "required": ["componentId"],
                                  "properties": {
                                    "colId": { "type": "string" },
                                    "componentId": {
                                      "type": "string",
                                      "minLength": 1
                                    }
                                  }
                                }
                              }
                            }
                }
              }
            }
          }
        } 

        static get MOBILE_COMPATIBLE() {
            return [
                'checkbox_selection',
                'dropdown_selection',
                'form_header',
                'hr',
                'label_text',
                'adalia_me_location_mobile',
                'adalia_me_location',
                'long_text_field',
                'radio_selection',
                'text_field',
                'number_field',
                'text',
                'title',
                'adalia_me_submission_title',
                'adalia_me_mobile_photo',
                'adalia_me_calculation',
                'optional_region',
                'dropdown_selection_with_search'
            ].indexOf(this.TYPE) > -1;
        }

        static icon(){
            return null;
        }
        
        static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = '') {
          if (!content) { return [] }
          if (!this.ANSWER_DEPENDENT) { return [] }
          let names = [];
          // Add the current component
          if (content.name) { names.push(repeatedFieldScope + '[' + content.name + ']') }

          // Check for child components recursively
          const childNames = (this.nestedComponents(content).map(c => Resolver.componentClass(c.type).resolveRelatedAnswerNamesOrIds(c, repeatedFieldScope)) || []).flat();

          return [...names, ...childNames];
        }
        

        static defaultContent(language) {
            return {
                id: 'id-' + Helpers.randomString(16),
                type: this.TYPE,
                containsSensitiveInformation: false,
                translations: this.defaultContentTranslations(language)
            };
        }

        static defaultContentTranslations(language) {
            let translations = {};
            translations[language] = {};

            for (let key in this.TRANSLATED_ATTRIBUTES || {}) {
                translations[language][key] = this.TRANSLATED_ATTRIBUTES[key] ? I18n.t(this.TRANSLATED_ATTRIBUTES[key], { scope: 'fe', locale: language }) : '';
            }
            return translations;
        }

        static nestedComponents(parentComponentContent) {
            return [];
        }

        static deleteNestedComponent(componentContent, componentId) {
            return false;
        }

        static dropComponent(dropArea, targetContent, droppedContent, components, isNested) {
            return false;
        }

        static disallowNesting() {
            return this.disallowMultiple() || false;
        }

        static disallowMultiple() {
            return false;
        }

        static dropAllowed(dropArea, droppedContent, components, isNested) {
            if (isNested && this.disallowNesting()) {
                // Disallow dropping to anything nested
                alert(I18n.t('alert_nested_' + this.TYPE, {scope: 'fe'}));
                return false;
            }

            if (components != undefined && this.disallowMultiple()) {
                for (var i = 0; i < components.length; i++) {
                    const comp = components[i];
                    if (
                      [
                        comp,
                        ...Resolver.componentClass(comp.type).nestedComponents(components[i]),
                      ].find((c) => c.type == this.TYPE && c.id != "component-drag-placeholder")
                    ) {
                      alert(I18n.t('alert_multiple_' + this.TYPE, {scope: 'fe'}));
                      return false;
                    }
                }
            }

            return true;
        }

        static isNested () {
            return false;
        }

        static clone(componentContent) {
            let cloneable = true;
            let clonedContent = JSON.parse(JSON.stringify(componentContent));
            let fieldNamePrefixRE = /^clone[^_]{8}_/;
            let prefixMap = {};

            // Generate new IDs and field names for the cloned content and clear reportKeys
            [clonedContent].concat(this.nestedComponents(clonedContent)).forEach((c) => {
                if (c.cloneable !== false && cloneable) {
                    c.id = 'id-' + Helpers.randomString(16);
                    c.reportKey = '';
                    // Field names are too fragile to just generate new random name but prefixing should be safe
                    if (c.name) {
                        // Some composition components might intentionally have equal name for different parts of the composed component.
                        // Therefore prefixMap makes sure that the equality is not broken upon clone.
                        prefixMap[c.name] = prefixMap[c.name] || 'clone' + Helpers.randomString(8) + '_';
                        c.name = c.name.match(fieldNamePrefixRE) ? c.name.replace(fieldNamePrefixRE, prefixMap[c.name]) : prefixMap[c.name] + c.name;
                    }
                } else {
                    cloneable = false;
                }
            });

            if (cloneable) {
                return clonedContent;
            }
            return false;
        }

        static deprecated() {
            return false
        }

          static compatiblePrefillComponents() {
            return [];
          }
        
          static compatiblePrefillDataTypes() {
            return [];
          }

        constructor(props) {
            super(props);
            this.inEditMode = (this.props.mode == Mode.EDIT);
            this.componentTitle = this.props.componentTitle;
            this.selectComponent = this.selectComponent.bind(this);
            this.updateSettings = this.updateSettings.bind(this);
            this.startDrag = this.startDrag.bind(this);
            this.deleteComponent = this.deleteComponent.bind(this);
            this.cloneComponent = this.cloneComponent.bind(this);
            this.preventEnterSubmit = this.preventEnterSubmit.bind(this);
            this.updateAnswer = this.updateAnswer.bind(this);
            this.state = {}
            this.prefillIconRef = React.createRef();
            this.datagridPrefillAnswer = null;
            this.configValidator = new Validator();
            this.allowModifyInSafeMode = (!this.props.safeMode || this.props.content?.isNewComponent == true);
        }


        
        /** 
         * Render view based on mode.
         * Child classes should implement renderView and renderSettings.
         * 
         * @fires componentClicked
         */
        render() {  
            switch (this.props.mode) {
                case 'toolbar-item':
                    const showIconOnly = this.props.showIconOnly
                    return (
                        <div ref="root" className={"component noselect" + (showIconOnly ? " center-text" : "")} onMouseDown={this.startDrag}>
                            {this.constructor.icon()} <span style={{paddingLeft: '5px'}}>{showIconOnly ? null : this.componentTitle ? this.componentTitle : I18n.t('component_title_' + this.constructor.TYPE, {scope: 'fe'})}</span>
                            {this.constructor.MOBILE_COMPATIBLE
                            ? <span className='mobile-compat-icon' title='This form component can be used to collect data using mobile device.'>
                                <i className="fa fa-mobile"></i>
                            </span>
                            : null}
                            <br/>
                            {this.constructor.deprecated() 
                                ?   <Tooltip trigger='hover' title={I18n.t('toolbar_item_deprecation_warning')} options={{ placement: "right" }}> 
                                        <span className="description">{I18n.t('deprecated')}</span> 
                                    </Tooltip> 
                                : <></>
                            }
                        </div>
                    );
                case 'settings':
                    let actionBtns = [];

                    if (this.props.content.deletable !== false) {
                        if (this.props.content.editable !== false && this.props.content.cloneable !== false) {
                            actionBtns.push(
                                <a key="clone" href="#" className="btn btn-primary" onClick={this.cloneComponent}>
                                    <i className="fa fa-clone"></i> <span>{I18n.t('clone_component', {scope: 'fe'})}</span>
                                </a>
                            );
                        }
                        if (this.allowModifyInSafeMode) {
                            actionBtns.push(
                                <a key="delete" href="#" className="btn btn-danger" onClick={this.deleteComponent}>
                                    <i className="icon-trash"></i> <span>{I18n.t('delete_component', {scope: 'fe'})}</span>
                                </a>
                            );
                        }
                    }
                    let actions = (actionBtns.length > 0) ? <div className="actions">{actionBtns}</div> : null;
                    return (
                        <div ref="root" data-id={this.props.content.id}>
                            <h1 ref="header">{I18n.t('component_title_' + this.constructor.TYPE, {scope: 'fe'})}</h1>
                            {this.props.content.configurationErrors?.length > 0 && <InvalidConfigMessage errors={this.props.content.configurationErrors} />}
                            <form ref={this.settingsFormRef()}>
                                {this.renderSettings ? this.renderSettings() : null}
                                {this.renderAdvancedSettings()}
                            </form>
                            {actions}
                        </div>
                    );
                default:
                    let sortHandle = null;
                    const prettyView = (this.renderPrettyView && this.props.mode == Mode.VIEW && this.props.viewMode == ViewMode.PRETTY) ? true : false;
                    if (this.inEditMode && this.props.content.sortable !== false) {
                        sortHandle = (
                            <div className="sort-handle-pos">
                                <Helpers.SortHandle startDragCallback={this.startDrag} />
                            </div>
                        );
                    }

                    return (
                      <div
                        ref="root"
                        className={this.containerClassNames().join(" ")}
                        data-type={this.constructor.TYPE}
                        data-id={this.props.content.id}
                        data-sort-order={this.props.sortOrder || 0}
                        onMouseDown={this.selectComponent}
                        onMouseUp={this.triggerComponentClickedEvent}
                        style={{
                            padding: prettyView && this.renderPrettyView() == null ? '0px' : undefined,
                            display: (this.props.mode == Mode.FILL || this.props.mode == Mode.VIEW) && this.props.content.hiddenField && !this.props.showHiddenFields ? 'none' : undefined,
                        }}
                      >
                        {sortHandle}
                        {this.renderComponent(prettyView)}
                        {this.inEditMode && this.props.content.hiddenField && (
                            <div className="hidden-field-overlay">
                                <i className="fa fa-eye-slash"></i>
                            </div>
                        )}
                        {this.props.mode == Mode.FILL && this.isReadOnlyAnswer() && !this.fullFieldName(false).includes('undefined') ? (
                          <input type="hidden" name={this.fullFieldName(false, "_readOnly")} value={true} />
                        ) : null}
                      </div>
                    );
            }
        }

        renderComponent(prettyView) {

          if (this.props.content.configurationErrors?.length > 0){
            return <InvalidConfigComponentPlaceholder label={this.getText('label')} />
          } 

          if (prettyView) return this.renderPrettyView();

          if (this.renderView) return this.renderView()
            
          return null;
        }

        componentDidMount() {
            if (this.props.mode == Mode.SETTINGS) {
                // Build path to current component
                let current = jQuery('.form-component[data-id="' + this.props.content.id + '"]').filter('.form-editor .panes .form .form-component');
                let header = jQuery(this.refs.header);

                current.parents('.form-component').each((i, p) => {
                    let title = I18n.t('component_title_' + p.getAttribute('data-type'), {scope: 'fe'});
                    let elId = p.getAttribute('data-id');
                    if (jQuery(`a[data-id="${elId}"]`).length == 0) header.prepend('<a href="#" data-id="' + elId + '">' + title + '</a> / ');
                });
                header.on('click', 'a', (e) => {
                    this.props.selectCallback(e.currentTarget.getAttribute('data-id'));
                    return false;
                });
            }
            this.updateDOMAfterUpdate();
        }

        triggerComponentClickedEvent = (event) => {
          if (typeof this.props.triggerEvent !== 'function') return;

          this.props.triggerEvent('componentClicked', {content: this.props.content, event, fullFieldName: this.fullFieldName()})
        }

        shouldComponentUpdate = (nextProps, nextState) => {
          const answerCheck = (prevAnswers, newAnswers) => {
            const dependencyArray = this.constructor.resolveRelatedAnswerNamesOrIds(this.props.content, this.props.repeatedFieldScope)
              .map(d => mapNameFromId(d, nextProps.findComponentsCallback)).filter(Boolean);

            return dependencyArray.some(dep => {
              return !shallowCompareAtPath(prevAnswers, newAnswers, dep)
            })
          }

          return shouldUpdate(
            this.props, 
            nextProps, 
            this.state, 
            nextState,
            answerCheck
          );            
        }

        componentDidUpdate() {
            this.updateDOMAfterUpdate();

            if (this.props.answersChangedCallback){
              this.props.answersChangedCallback();
            }

            this.updatePrefillIcon();
        }

        // When a component is selected (clicked on) in edit mode
        selectComponent(event) {
            if (this.inEditMode) {
                this.props.selectCallback(this.props.content);
            }
            event.stopPropagation();
        }

        // When a component is deleted in edit mode
        deleteComponent(event) {
            if (this.props.deleteCallback && confirm(I18n.t('confirm_delete_component', {scope: 'fe'}))) {
                this.props.deleteCallback(this.props.content.id);
            }
            event.preventDefault();
        }

        // When a component is cloned in edit mode
        cloneComponent(event) {
            if (this.props.cloneCallback) {
                this.props.cloneCallback(this.props.content.id);
            }
            event.preventDefault();
        }

        // When settings are changed
        updateSettings(event) {
            let content = {id: this.props.content.id, translations: JSON.parse(JSON.stringify(this.props.content.translations)) || {}};

            jQuery(this.refs[this.settingsFormRef()]).serializeArray().forEach((s) => {
                const invalidInput = s.name == 'reportKey' && !/^[A-Za-z0-9 \-._,]*$/.test(s.value)
                if(invalidInput && event.type == 'blur'){
                    alert('Report key only allows numbers/letters/spaces/dashes/dots/underscores/commas')
                }
                if (typeof this.constructor.TRANSLATED_ATTRIBUTES[s.name] != 'undefined') {
                    content.translations[this.props.language] = content.translations[this.props.language] || {};
                    content.translations[this.props.language][s.name] = s.value;
                } else {
                    content[s.name] = s.value;
                }
            });
            // Calling this removes the options if the form has already disappeared.
            if (this.beforeUpdateSettings && this.refs[this.settingsFormRef()]) {
                this.beforeUpdateSettings(content);
            }

            content.configurationErrors = this.validateAgainstJsonSchema(content);

            content.containsSensitiveInformation = castDataType(content.containsSensitiveInformation, DataType.BOOLEAN);
            content.hiddenField = castDataType(content.hiddenField, DataType.BOOLEAN);
            this.props.updateSettingsCallback(content.id, content, true);
        }

        validateAgainstJsonSchema(content) {
            // Validate the logic of datagrid context columns to not break form on invalid confgis
            // The base is a work around for validate not working in Chrome 129. If you change to the schema that has partial URL, please read docs to adjust accordingly
            const configValidationResult = this.configValidator.validate(content, this.constructor.CONFIG_VALIDATION_SCHEMA, {"base": "https://example.com"})
            if (configValidationResult.errors.length > 0 ) {
              console.warn(configValidationResult.toString());              
              return configValidationResult.errors
                .map(e =>({name: e.name, path: e.path, argument: e.argument}));
            } else {
              return null;
            }
        }

        // When component drag is started
        startDrag(event) {
            if (this.props.startDragCallback) {
                this.props.startDragCallback(event, this.constructor, this.props.content, this.props.mode, {
                    width: jQuery(this.refs.root).outerWidth(),
                    height: jQuery(this.refs.root).outerHeight()
                });
            }
            event.preventDefault();
        }

        containerClassNames() {
            let classNames = ['form-component', this.constructor.TYPE, this.props.mode];

            if (this.inEditMode && this.props.selectedId == this.props.content.id) {
                classNames.push('selected');
            }
            if (this.inEditMode && this.props.content.sortable !== false) {
                classNames.push('sortable');
            }

            if (this.props.content.configurationErrors?.length > 0) {
              classNames.push('bg-danger')
            }

            if (this.inEditMode && this.props.content.hiddenField) {
                classNames.push('hidden-field');
            }

            return classNames;
        }

        settingsFormRef() {
            return 'settings-' + this.props.content.id;
        }

        helpIcon() {
            return (this.getText('help') || '').length > 0 ? <span className="help" data-title={this.getText('help')}>?</span> : null
        }

        descriptionText() {
            return (this.getText('description') || '').length > 0 ? (<span className="description" dangerouslySetInnerHTML={{__html: Helpers.linkURLs(this.getText('description'))}}></span>) : null
        }

        fullFieldName(isArray, endfix) {
          return Helpers.fullFieldName(this.props.fieldScope, this.props.repeatedFieldScope, this.props.content.name,  isArray, endfix);
        }

        getAnswer(context) {
            return castDataType(this.getRawAnswer(context), this.props.content.dataType || DataType.STRING);
        }

        getRawAnswer(context) {
            if (this.props.hideSensitiveInformation && this.props.content.containsSensitiveInformation) {
                return this.sensitiveInformationDefault();
            }
            
            let fieldNamePath = Helpers.fieldNamePath(this.fullFieldName(), this.props.fieldScope);
            let answer = Helpers.getObjectValue(this.props.answers || {}, fieldNamePath);
            let prefilledAnswer = this.getPrefilledAnswer(context);

            return (this.isReadOnlyPrefilledAnswer(context) || (answer === void 0 && this.hasPrefilledAnswer(context))) ? prefilledAnswer : (answer == undefined || answer == null ? this.rawAnswerDefault() : answer);
        }

        // Returns true if form has been saved as with a prefilled (read only) value.
        // Can be used to determine if saved answer was initially locked, to prevent unlocking it when using answers.
        answerSavedAsReadOnly() {
            let fieldNamePath = this.fullFieldName(false, "_readOnly").split(/[\[\]]+/).filter((s) => (['', this.props.fieldScope].indexOf(s) == -1));
            return castDataType(Helpers.getObjectValue(this.props.answers || {}, fieldNamePath), DataType.BOOLEAN);
        }

        getValidationLabel() {
            let text = this.getText(this.props.content.label) || '';
            return text.length > 0 ? text : I18n.t('form_field');
        }

        renderPrefillIcon(inputIcon) {
            if (!this.showPrefillIcon()) return null;

            const hasLabelRow = this.getText("label") || this.props.content.required || this.helpIcon();
            const className = `${inputIcon ? "input" : "prefill"}-icon ${hasLabelRow ? "ml-1" : ""}`

            if (this.mayUsePrefillAnswer()){
                return <Tooltip
                    ref={this.prefillIconRef}
                    className={`fa fa-pencil-square-o use-prefill ${className}`}
                    title={this.getFormattedPrefillAnswer().length > 0 ? I18n.t("click_to_use_prefilled_value_with_value", {
                        scope: "fe",
                        value: this.getFormattedPrefillAnswer(),
                    }) : I18n.t("click_to_use_prefilled_value", { scope: "fe" })}
                    onClick={() => this.usePrefilledValue()}
                    options={{ placement: inputIcon ? "top" : "right"  }}
                />
            }
            return <Tooltip
                ref={this.prefillIconRef}
                className={`fa fa-check-square-o ${className}`}
                title={I18n.t("prefilled_value_matches", { scope: "fe" })}
                options={{ placement: inputIcon ? "top" : "right"  }}
            />
        }

        updatePrefillIcon() {
            if (this.prefillIconRef.current) {
                const prefillIcon = jQuery(this.prefillIconRef.current);
                const inputGroup = prefillIcon.closest(".input-group");
                if (inputGroup.length > 0) {
                    const input = inputGroup.find("input");
                    if (input.length > 0 && inputGroup.outerWidth(true) > input.outerWidth(true)) {
                        prefillIcon.css("right", inputGroup.outerWidth(true) - input.outerWidth(true) + 7)
                    }
                }
            }
        }

        // @deprecated — resolved by datagrid_dependency_resolver
        shouldUpdateFromDatagrid(prevProps, lookupBy) {
          // No dependencies and answer already set
          if (this.props.content.dependencyMapping.length == 0 && this.getRawAnswer()) {
            return false;
          }
          if (prevProps === undefined){ // AKA MOUNT
            if (this.getRawAnswer()) return false; // Received saved answer from props
            return true;
          } 
          let prevLookupBy = resolveLookupKeys(this.props.content.dependencyMapping, this.props.fieldScope, this.props.repeatedFieldScope, prevProps?.answers || {})

          return !equal(lookupBy, prevLookupBy)
        }

        // @deprecated — resolved by datagrid_dependency_resolver
        updateAnswerFromDatagrid(prevProps, opts = {force: false, skipPrefill: false}) {
          // Some other state/prop update than answers. Ignore
          if (prevProps && prevProps.answers === this.props.answers) return;

          if (this.props.content.useContextual == CONTEXT_OPTIONS.DATAGRID_PRESET && !_.isEmpty(this.context.datagridRows)) {
            this.updateAnswerFromDatagridPreset(prevProps, opts);
          }
          if (this.props.content.useContextual == CONTEXT_OPTIONS.DATAGRID_ROW) {
            this.updateAnswerFromDatagridRowLookup(prevProps, opts)
          }
        }

        // @deprecated — resolved by datagrid_dependency_resolver
        updateAnswerFromDatagridRowLookup(prevProps, opts) {
          let dependencyComponentName = null;
          if (this.props.findComponentsCallback && this.props.content.datagridRowLookupDependency) {
            let comp = this.props.findComponentsCallback({ id: this.props.content.datagridRowLookupDependency })[0]
            dependencyComponentName = comp.name;
          }

          const {linkedAnswerWithinRegion, linkedAnswerOutside} = resolveAnswer(this.props.fieldScope, this.props.repeatedFieldScope, dependencyComponentName, this.props.answers);
          const answer = linkedAnswerWithinRegion || linkedAnswerOutside
          const depAnswer = answer?.[this.props.content.columnAccessor];

          if(!opts.skipPrefill && depAnswer && this.state.datagridPrefillAnswer != depAnswer) {
            this.setState({
              ...this.state,
              datagridPrefillAnswer: `${depAnswer}` // Prefill expects a string...
            });
          }

          if (prevProps === undefined) {
            return;
          }

          const {linkedAnswerWithinRegion: prevLinkedAnswerWithinRegion, linkedAnswerOutside: prevLinkedAnswerOutside} = resolveAnswer(this.props.fieldScope, this.props.repeatedFieldScope, dependencyComponentName, prevProps.answers);
          const prevAnswer = prevLinkedAnswerWithinRegion || prevLinkedAnswerOutside
          const prevDepAnswer = prevAnswer?.[this.props.content.columnAccessor];

          // Depedency changed
          if (depAnswer != prevDepAnswer) {
            const newAnswer =
              depAnswer?.constructor === Object && this.props.content.type === 'text_field'
                  ? depAnswer.label : depAnswer;
            this.updateAnswer(newAnswer);
            return;
          }
        }
        
        // @deprecated — resolved by datagrid_dependency_resolver
        updateAnswerFromDatagridPreset(prevProps, opts = {force: false, skipPrefill: false}) {
          const {datagridId, presetId, columnAccessor, dependencyMapping = []} = this.props.content;
          let lookupBy = resolveLookupKeys(dependencyMapping, this.props.fieldScope, this.props.repeatedFieldScope, this.props.answers)
          if (lookupBy == undefined) return;
          const rows = this.context.datagridRows[datagridId]?.[presetId]?.rows || [];            
          let currentAnswer = this.getRawAnswer(); // Answer before setting prefil
          let answer = this.loadAnswerFromDatagrid(lookupBy, dependencyMapping, rows, columnAccessor);

          if(!opts.skipPrefill && this.state.datagridPrefillAnswer != `${answer}`){
            this.setState({
              ...this.state,
              datagridPrefillAnswer: `${answer}` // Prefill expects a string...
            }); 
          }

          if(!opts.force && !this.shouldUpdateFromDatagrid(prevProps, lookupBy)){
            return;
          }

          if (dependencyMapping.length == 0 && currentAnswer ) {
            // Only load the initial answer when no answer.
            return;
          }
          if (answer == this.getRawAnswer()) return;
          this.updateAnswer(answer);
      }

        // @deprecated — resolved by datagrid_dependency_resolver
        loadAnswerFromDatagrid(lookupBy, dependencyMapping, rows, columnAccessor) {
          if (Object.keys(lookupBy).length != dependencyMapping.length){
            return this.rawAnswerDefault();
          }

          // Takes the first row in case of blank lookupBy
          const loadFrom = rows.find(row => {
            return Object.keys(lookupBy).every(colId => row[colId] == lookupBy[colId])
          })

          if (!loadFrom) {
            return '#NOVALUE';
          }

          if (loadFrom && loadFrom[columnAccessor]){
            return loadFrom[columnAccessor];
          }
        }

        usePrefilledValue() {
          this.updateAnswer(this.getPrefilledAnswer());
        }

        mayUsePrefillAnswer() {
            const prefillAnswer = this.getPrefilledAnswer();
            const answer = this.getRawAnswer();

            if (Array.isArray(prefillAnswer) && Array.isArray(answer)) {
                return (
                  prefillAnswer.some((a) => !answer.includes(a)) ||
                  answer.some((a) => !prefillAnswer.includes(a))
                );
            }

            if (_.isObject(prefillAnswer) || _.isObject(answer)) {
              return !_.isEqual(prefillAnswer, answer);
            }
            return prefillAnswer != answer;
        }

        showPrefillIcon() {
            return this.hasPrefilledAnswer() && !this.isReadOnlyAnswer();
        }

        hasPrefilledAnswer(context) {
            return this.getPrefilledAnswer(context).length > 0;
        }

        getReportKey() {
            return this.props.content?.reportKey;
        }

        getPrefillProjectId(context) {
            // You can provide context in as params (this.context is not initialized in constructor)
            return context?.projectId || this.props.project?.id || this.props.projectId || this.context?.projectId;
        }

        getPrefilledAnswer(context) {
            // Look for project-specific prefill answers if projectId is defined somehow, fallback to general prefillAnswers
            const prefillAnswers = (this.props.prefillAnswers || {})[this.getPrefillProjectId(context)] || this.props.prefillAnswers || {};
            return (prefillAnswers[this.getReportKey()] || [])[0] || this.state.datagridPrefillAnswer || this.rawAnswerDefault();
        }

        getAutoComplete() {
          return this.props.autoComplete || 'off';
        }

        getPrefilledAttachments(context) {
            const prefillAttachments = (this.props.prefillAttachments || {})[this.getPrefillProjectId(context)] || this.props.prefillAttachments || {};
            return prefillAttachments[this.getReportKey()] || [];
        }

        getFormattedPrefillAnswer(context) {
            return this.getPrefilledAnswer(context);
        }

        rawAnswerDefault() {
            return '';
        }

        sensitiveInformationDefault() {
            return I18n.t('info_withheld');
        }

        lockedInformationDefault() {
            return (<span className="locked-info">{I18n.t('info_locked')}</span>);
        }

        isFillMode() {
            return this.props.mode == Mode.FILL;
        }

        modeFillOrView() {
            return this.props.mode == Mode.FILL || this.props.mode == Mode.VIEW;
        }

        getInputClassNames() {
            let inputClassName = this.props.mode == Mode.FILL && this.isReadOnlyAnswer() ? 'cursor-not-allowed ' : '';
            // User input readonly field when not prefilled answer and readonly
            inputClassName += (this.modeFillOrView() && !this.isReadOnlyPrefilledAnswer() && !this.isLockedSettingEnabled()) ? 'user-input-field ' : '';
            return inputClassName;
        }

        getLockedInformationTooltip = (isLocked, children) => {
            if (!isLocked) return <>{children}</>;
            return (
                <TooltipWithRef title={I18n.t('field_populated_and_locked')} className={`form-tooltip-locked-container`}>
                    {children}
                </TooltipWithRef>
            );
        };

        isReadOnlyAnswer(context) {
          if (this.props.disableEditLocks) {
            return false;
          }
            return this.props.readOnly || this.props.content.readOnly || this.isLockedSettingEnabled() || this.isReadOnlyPrefilledAnswer(context) || this.props.lockFormFill;
        }

        isLockedSettingEnabled() {
          return this.props.content.lockEdit;
        }

        isReadOnlyPrefilledAnswer(context) {
            return (
                (this.hasPrefilledAnswer(context)) &&
                this.props.content.lockEditWhenPrefilled &&
                !this.props.disablePrefillEditLocks
            );
        }

        updateAnswer(answer) {
            if (this.props.updateAnswerCallback) {
                this.props.updateAnswerCallback(this.fullFieldName(), answer === void 0 ? this.readAnswerFromInputs() : answer);
            }
        }

        getRequiredFieldProgressByPage() {
          return this.props.requiredFieldProgressByPage || {};
        }

        getPageValidationFailures() {
          return this.props.formValidationFailures?.pages || {};
        }

        getFormValidationFailures() {
          const { name } = this.props.content;
          const { repeatedFieldScope } = this.props;
          // Getting name similarly as in form viewer so that
          // validations performed on form level are matched properly
          const fullName = Helpers.fullFieldName(this.props.fieldScope, repeatedFieldScope, name);
          return this.props.formValidationFailures?.fields?.[fullName] || [];
        }

        readAnswerFromInputs() {
            if (!this.props.content) {
                return null;
            }
            
            let answer = null;
            let inputs = jQuery(this.refs.root).find('input, select, textarea').filter('[name^="' + this.fullFieldName() + '"]');

            inputs.serializeArray().forEach((param) => {
                if (param.name.match(/\[\]$/)) {
                    answer = answer || [];
                    answer.push(param.value);
                } else {
                    answer = param.value;
                }
            });
            return answer;
        }

        preventEnterSubmit(event) {
            if (event.which === 13) {
                event.preventDefault();
            }
        }

        getText(key, fallback = '') {
            return Helpers.getText((this.props.content || {}).translations, key, this.props.language, fallback);
        }

        updateDOMAfterUpdate() {
            if (this.props.mode == Mode.EDIT && this.props.content.id == this.props.selectedId) {
                jQuery('.form-component.sortable').removeClass('with-sort-handle');
                jQuery(this.refs.root).closest('.sortable').addClass('with-sort-handle');
            }

            if (this.props.mode == Mode.FILL) {
                jQuery('[data-validation="false"]').tooltip('destroy');
                jQuery('[data-validation="true"]').tooltip({
                    trigger: 'focus',
                    placement: 'bottom'
                }).tooltip('fixTitle');
                // jQuery('[data-validation="true"]').tooltip('show');
            }
        }

        getOptionsForComponentSelect() {
            return [[this.props.content.id, this.getText(this.props.content.label)]];
        }

        createNewComponentInstance(c, props) {
            const cClass = Resolver.componentClassName(c.type);
            return new FormComponent[cClass]({ content: c, ...props });
        }

        // Start of functions used in datagrid contextual mode
        // TODO: Move to separate file
        isDatagridContextAvailable() {
            return this.context?.datagridContext?.length > 0;
        }

        useOptionSetHierarchy() {
          return this.props.content?.useOptionSetHierarchy;
        }

        useOptionLibrary() {
          return this.props.content?.useOptionLibrary;
        }

        useDatagridContextual() {
            return [CONTEXT_OPTIONS.DATAGRID_PRESET, CONTEXT_OPTIONS.DATAGRID_ROW].includes(this.props.content?.useContextual);
        }

        getDropdownDatagridOptions() {
            return [['', `${I18n.t('datagrid')}...`]].concat(this.context.datagridContext?.map((i) => [i.id, i.name]) || []);
        }

      getDropdownDatagridPresetOptions(presetFilter = (_) => true) {
        let options = [
          [
            '',
            `${I18n.t('preset')}...`
          ]
        ]
        
        let presetOptions = this.context.datagridContext
            ?.find(v => v.id == this.props.content.datagridId)
            ?.presets
            ?.filter(presetFilter)
            ?.map((i) => [i.id, i.name])

        return options.concat(presetOptions || [])
      }

        getDropdownDatagridColumnOptions(datagridId) {
            return [['', `${I18n.t('column')}...`], ['row_id', I18n.t('id')]].concat(this.context.datagridContext?.find(v => v.id == datagridId)?.columns?.map((i) => [i.accessor, i.name]) || []);
        }

        getSelectedDatagridPreset() {
            let {datagridId, presetId} = this.props.content;
            return this.context.datagridContext?.find(v => v.id == datagridId)?.presets?.find(v => v.id == presetId)
        }

        getSelectedDatagridColumn() {
            let {datagridId, columnAccessor} = this.props.content;
            return this.context.datagridContext?.find(v => v.id == datagridId)?.columns?.find(v => v.accessor == columnAccessor)
        }

        renderDatagridContextPresetMappingSettings(allGroups = false) {
            let {datagridId, columnAccessor, dependencyMapping = []} = this.props.content;
            let selectedPreset = this.getSelectedDatagridPreset()
            let components = []

            if (allGroups || selectedPreset?.columns?.[columnAccessor]?.rowGroup) {
                let parentGroups = selectedPreset ? Object.values(selectedPreset.columns).filter(v => v.rowGroup) : [];
                if (!allGroups) {
                    parentGroups = parentGroups.filter(v => v.rowGroupIndex < selectedPreset.columns[columnAccessor].rowGroupIndex);
                }

                components = parentGroups.sort((a, b) => a.rowGroupIndex - b.rowGroupIndex).map((col, idx) => {
                    let selectedColumn = this.context.datagridContext?.find(v => v.id == datagridId)?.columns?.find(v => v.accessor == col.colId)
                    if (!selectedColumn) return null;

                    return (
                        <div className='option' key={col.colId}>
                            <div className='option-label form-control'>
                                {selectedColumn.name}
                            </div>
                            <div className='option-label'>
                                <select name={`dependencyMapping|${col.colId}|${idx}|componentId`} className='form-control' defaultValue={dependencyMapping?.[idx]?.['componentId']||''} onChange={this.updateSettings} autoComplete={this.getAutoComplete()}>
                                    {this.getComponentSelectionOptions(
                                      Object.values(DataType),
                                      ['text_field', 'dropdown_selection', 'number_field', 'radio_selection'] // Basic components with single value
                                    ).map((o) => (
                                        <option key={o[0]} value={o[0]}>{o[1]}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    );
                });
            }

            return (components.length > 0 ?
                    <div className='setting'>
                        <label>{I18n.t('dependencies')}</label>
                        <span className='description'>{I18n.t('datagrid_dependencies_description', {scope: 'fe'})}</span>
                        <div className='option-groups'>
                            <div className='option-group'>
                                <div className='group-head' style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem'}}>
                                    <strong>{I18n.t('datagrid_column', {scope: 'fe'})}</strong>
                                    <strong>{I18n.t('form_field', {scope: 'fe'})}</strong>
                                </div>
                                <div className='group-body'>
                                    <div className='options'>{components}</div>
                                </div>
                            </div>
                        </div>
                    </div> : <></>
            );
        }

        renderLookupComponentSelection() {
          let options = [['']];

          if (this.props.findComponentsCallback) {
            options = options.concat(this.props.findComponentsCallback({ className: 'DatagridRowLookup' }).map((c) => {
              let label = Helpers.getText(c.translations, c.label, this.props.language);
              return [
                c.id,
                (label != '') ? label : (c.reportKey || '' != '') ? c.reportKey : '[ ' + I18n.t('no_label_or_key', {scope: 'fe'}) + ' ]'
              ];
            }));
          }
          return  <div className='setting'>
            <label>{I18n.t('component_title_adalia_me_datagrid_row_lookup', {scope: 'fe'})}</label>
            <select name={'datagridRowLookupDependency'} className='form-control' defaultValue={this.props.content.datagridRowLookupDependency} {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})} autoComplete={this.getAutoComplete()}>
              {options.map((o) => (
                  <option key={o[0]} value={o[0]}>{o[1]}</option>
              ))}
            </select>
            <span className='description'>{I18n.t('row_lookup_component_description', {scope: 'fe'})}</span>
          </div>
        }

        renderDatagridContextSettings(allGroups = false) {
            return (
                <>
                    <Setting.Dropdown 
                      name='useContextual' 
                      label={I18n.t('field_use_contextual', {scope: 'fe'})} 
                      value={this.props.content.useContextual}
                      options={Object.values(CONTEXT_OPTIONS).map(o => [o, I18n.t(o)])}
                      description={I18n.t('contextual_description', {scope: 'fe'})}
                      {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                    />
                    <div className='contextual-settings'>
                      {this.renderContextSettingsByType(this.props.content.useContextual, allGroups)}
                    </div>
                </>
            );
        }

        renderContextSettingsByType(context, allGroups = false) {
          if (!context || context == CONTEXT_OPTIONS.NONE) {
            return null;
          } 
          if (context == CONTEXT_OPTIONS.DATAGRID_PRESET){
            return <>
              {this.renderDatagridPresetSelection()}
              {this.renderDatagridColumnSelection(this.props.content.datagridId)}
              {this.renderDatagridContextPresetMappingSettings(allGroups)}
          </>
          }
          if (context == CONTEXT_OPTIONS.DATAGRID_ROW){
            // Context is defined by the datagrid row selection component. Load the datagridId from the selected component
            let datagridId = null;
            if (this.props.findComponentsCallback && this.props.content.datagridRowLookupDependency) {
              let comp = this.props.findComponentsCallback({ id: this.props.content.datagridRowLookupDependency })[0]
              datagridId = comp.datagridId;
            }
            return <>
              {this.renderLookupComponentSelection()}
              {this.renderDatagridColumnSelection(datagridId)}
          </>
          }
        }

        renderDatagridPresetSelection( opts = {
          description: I18n.t('datagrid_preset_selection_description', {scope: 'fe'}), 
          presetFilter: (_) => true
        }
        ) {
          const {datagridId, presetId} = this.props.content;

          const label = <>
            {I18n.t('field_selected_datagrid_preset', {scope: 'fe'})}
            {datagridId && presetId &&
              <a href={datagrid_show_path({id: datagridId, preset_id: presetId, project_id: this.context?.projectId, _options: true})} target='_blank' className='ml-1'>
                <i className='fa fa-arrow-up-right-from-square'/>
              </a>
            }
          </>;

          return <>
            {this.renderDatagridSelection()}
            <Setting.Dropdown
              name="presetId"
              label={label}
              value={this.props.content.presetId}
              options={this.getDropdownDatagridPresetOptions(opts.presetFilter)}
              description={opts.description}
              {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
            />
          </>
        }

        renderDatagridSelection() {
          const {datagridId} = this.props.content;

          const label = <>
            {I18n.t('field_selected_datagrid', {scope: 'fe'})}
            {datagridId &&
              <a href={datagrid_show_path({id: datagridId, project_id: this.context?.projectId, _options: true})} target='_blank' className='ml-1'>
                <i className='fa fa-arrow-up-right-from-square'/>
             </a>
            }
          </>;

          return <Setting.Dropdown
            name="datagridId"
            label={label}
            value={this.props.content.datagridId}
            options={this.getDropdownDatagridOptions()}
            {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
          />
        }
        renderDatagridColumnSelection(datagridId) {
          return <Setting.Dropdown
            name="columnAccessor"
            label={I18n.t('field_selected_datagrid_column', {scope: 'fe'})}
            value={this.props.content.columnAccessor}
            options={this.getDropdownDatagridColumnOptions(datagridId)}
            description={I18n.t('datagrid_column_selection_description', {scope : 'fe'})}
            {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
          />
        }
        // End of functions used in datagrid contextual mode


        // Legacy sources of prefill functionality.
        renderSourcesOfPrefillSettings() {
          if (!this.props.content.datagridId) {
            return;
          }
      
          return (
            <div className='setting'>
              <label>{I18n.t('field_source_of_prefill', {scope: 'fe'})}</label>
              <select name='sourcesOfPrefill' className="form-control" defaultValue={this.props.content.sourcesOfPrefill} onChange={this.updateSettings} autoComplete={this.getAutoComplete()}>
                {this.sourcesOfPrefillComponentOptions().map((o) => (
                  <option key={o[0]} value={o[0]}>{o[1]}</option>
                ))}
              </select>
            </div>
          );
        }

        sourcesOfPrefillComponentOptions() {
          return this.getComponentSelectionOptions(
            this.constructor.compatiblePrefillDataTypes(),
            this.constructor.compatiblePrefillComponents()
          )
        }

        renderAdvancedSettings() {
            return (
                <CollapsibleSection title={I18n.t('advanced_settings')} initialCollapsed={true}>
                    <Setting.Checkbox
                        name="hiddenField"
                        label={I18n.t('field_hidden_field', {scope: 'fe'})}
                        description={I18n.t('field_hidden_field_description', {scope: 'fe'})}
                        value={this.props.content.hiddenField}
                        onChange={this.updateSettings}
                    />
                </CollapsibleSection>
            );
        }

        renderPrettyViewSettings(showLabelSetting = true) {
          if (!this.renderPrettyView){
            return;
          }
          const { hide = false, show_label = false } = this.props.content.prettyView || {};
          // Hide for now. This is not really used by anybody and might clean up this functionality
          return (
              <CollapsibleSection title={I18n.t('pretty_mode')} style={{display: 'none'}}>
                <Setting.Checkbox name="prettyHide" label={I18n.t('hide')} value={hide} onChange={this.updateSettings} description={I18n.t('hide_pretty_description')} />
                {showLabelSetting &&
                  <Setting.Checkbox name="prettyShowLabel" label={I18n.t('show_label')} value={show_label} onChange={this.updateSettings} description={I18n.t('show_label_pretty_description')} />
                }
              </CollapsibleSection>
          )
        }

        beforeUpdateSettingsPrettyView(content){
          content.prettyView = {};
          content.prettyView.hide = castDataType(content.prettyHide, DataType.BOOLEAN);
          content.prettyView.show_label = castDataType(content.prettyShowLabel, DataType.BOOLEAN);
        }

        getComponentSelectionOptions(dataTypes, formComponentTypes) {
          let options = [[['', I18n.t('component_selection_placeholder', {scope: 'fe'})]]];
      
          if (this.props.findComponentsCallback) {
            [
              ...this.props.findComponentsCallback({
                  dataType: dataTypes,
              }),
            ].forEach((c) => {
              if (c.id != this.props.content.id && formComponentTypes.includes(c.type)) {
                  const fc = this.createNewComponentInstance(c, {});
                  options.push(this.getOptionsForSourceOfPrefillComponent(fc));
              }
            });
          }
          return options.flat(1);
        }
      
        getOptionsForSourceOfPrefillComponent(fc) {
          return [[fc.props.content.name, fc.getText(fc.props.content.label)]];
        }
        // End of legacy sources of prefill

        // Legacy datagrid connection rendering in option settings
        renderDataGridDetails() {
          return (
            <>
              { this.props.content.datagridName &&
                <>
                  <label>{I18n.t('datagrid')}</label>
                  <p>{this.props.content.datagridName}</p>
                </>
              }
              { this.props.content.colName &&
                <>
                  <label>{I18n.t('connected_column')}</label>
                  <p>{this.props.content.colName}</p>
                </>
              }
            </>
          );
        }
        // End of legacy datagrid connection rendering
}


// TODO: MOVE THESE
// Look for a field either within the current repeatedField scope, or outside of all scopes.
// Hate this, but other option would be to write some lookup logic whether a component is inside a repeated region
//   and as field name is unique works as well ¯\_(ツ)_/¯
export function resolveAnswer(fieldScope, repeatedFieldScope, componentId, answers) {
  let fullFieldName = Helpers.fullFieldName(fieldScope, repeatedFieldScope, componentId, false, null);
  let fieldNamePath = Helpers.fieldNamePath(fullFieldName, fieldScope);
  let linkedAnswerWithinRegion = Helpers.getObjectValue(answers || {}, fieldNamePath);

  fullFieldName = Helpers.fullFieldName(fieldScope, "", componentId, false, null);
  fieldNamePath = Helpers.fieldNamePath(fullFieldName, fieldScope);
  let linkedAnswerOutside = Helpers.getObjectValue(answers || {}, fieldNamePath);
  
  return {linkedAnswerWithinRegion, linkedAnswerOutside}
}

// For given dependency mapping and answers. Resolve the keys to look for in the given grid
export function resolveLookupKeys(dependencyMapping, fieldScope, repeatedFieldScope, answers){
  let lookupBy = {}
  let failure = false;
  if (dependencyMapping.length == 0) return lookupBy;
  // Filter datagrid rows based on their mapping dependencies
  // Check both inside and outside repeatable region, as we can't tell where the target is by field name
  //   Invalid option name, componentId is actually fieldName here
  dependencyMapping.forEach(({componentId, colId}) => {
      const {linkedAnswerWithinRegion, linkedAnswerOutside} = resolveAnswer(fieldScope, repeatedFieldScope, componentId, answers)
      
      if (!linkedAnswerWithinRegion && !linkedAnswerOutside) {
        failure = true;
        return; // All linked answers must be defined
      }
      lookupBy[colId] = linkedAnswerWithinRegion || linkedAnswerOutside;
  });

  return failure ? undefined : lookupBy
}
